import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CircularProgress, Stack } from "@mui/joy";
import DefaultPage from "pages/container/DefaultPage";
import useLoginPromotion from "hooks/useLoginPromotion";
import { PATH } from "routes/paths";

const KakaoLoginRedirectPromotion = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const code = searchParams.get("code");
  const hasCalledLogin = useRef(false);

  // 출처를 OAuth state 로 받아 취소/실패 시 돌아갈 로그인 화면을 결정한다.
  // (authorize 시 referral 진입이면 state="referral" 를 실어 보냈다.)
  const fallbackPath =
    searchParams.get("state") === "referral"
      ? PATH.referralLogin
      : PATH.promotionLogin;

  const { login } = useLoginPromotion({
    url: `/user/v2/login`,
    code: code!!,
    type: "kakao",
    redirectUri: `${window.location.origin}/promotion/oauth/kakao`,
  });

  useEffect(() => {
    if (!code) {
      console.error("[Kakao Login] Authorization code가 없습니다.");
      navigate(fallbackPath, { replace: true });
      return;
    }

    // 동일 인가 코드의 중복 제출 방지 (KOE320 방지).
    // useRef 는 컴포넌트 리마운트/동시 마운트 시 초기화되므로,
    // 코드 값 기준으로 sessionStorage 에 1회 제출 플래그를 둔다.
    const guardKey = `kakao_oauth_code_used:${code}`;
    if (hasCalledLogin.current || sessionStorage.getItem(guardKey)) return;

    // 즉시 플래그를 설정하여 동시 실행 방지
    hasCalledLogin.current = true;
    sessionStorage.setItem(guardKey, "1");

    // Authorization code를 사용한 후 URL에서 즉시 제거하여
    // 새로고침/뒤로가기 시 재사용 방지
    window.history.replaceState({}, document.title, "/promotion/oauth/kakao");

    // 로그인 실패(예: 만료/무효 code 로 인한 KOE320) 시 무한 로딩 대신
    // 로그인 페이지로 돌려보내 재시도(새 code 발급)할 수 있게 한다.
    login().catch(() => {
      navigate(fallbackPath, { replace: true });
    });
    // login과 navigate를 의존성에서 제거하여 불필요한 재실행 방지
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  return (
    <DefaultPage
      content={
        <Stack alignItems={"center"}>
          <CircularProgress color="neutral" />
        </Stack>
      }
    />
  );
};

export default KakaoLoginRedirectPromotion;
